import { LOCAL_STORAGE_KEYS, REMOVE_TOKENS, SECONDS_TO_REFRESH_TOKEN } from "../common/constants";
import { parseJWTToken, filterKeys } from "../common/utils";
import { googleLogout } from "@react-oauth/google";
import { fetchUserData } from "../services/auth.service";
// Imported lazily inside scheduleProactiveRefresh to avoid a circular
// dependency (client.ts imports Auth, Auth imports client.ts).
type SilentRefreshFn = () => Promise<void>;

export class Auth {
  private static instance: Auth;
  authenticated: boolean;
  isRefreshing: boolean;
  refreshSubscribers: Array<(token: string | null) => void>;
  accessToken: string | null;
  refreshToken: string | null;
  googleAuthToken: string | null;
  googleAccessToken: string | null;
  googleRefreshToken: string | null;
  userMetaData: {};
  private channel: BroadcastChannel | null = null;

  requiredDataKeys = [
    "email",
    "family_name",
    "given_name",
    "picture",
    "username",
  ];

  constructor() {
    this.authenticated = false;
    this.isRefreshing = false;
    this.refreshSubscribers = [];
    this.accessToken = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    this.refreshToken = localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
    this.googleAccessToken = localStorage.getItem(
      LOCAL_STORAGE_KEYS.GOOGLE_ACCESS_TOKEN
    );
    this.googleRefreshToken = localStorage.getItem(
      LOCAL_STORAGE_KEYS.GOOGLE_REFRESH_TOKEN
    );
    this.googleAuthToken = localStorage.getItem(
      LOCAL_STORAGE_KEYS.GOOGLE_AUTH_TOKEN
    );
    this.userMetaData = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_KEYS.USER_META_DATA) || "{}"
    );
  }

  public static getInstance(): Auth {
    return this.instance || (this.instance = new this());
  }

  getAccessToken() {
    return (
      this.accessToken || localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN)
    );
  }

  getRefreshToken() {
    return (
      this.refreshToken ||
      localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN)
    );
  }

  getGoogleAccessToken() {
    return (
      this.googleAccessToken ||
      localStorage.getItem(LOCAL_STORAGE_KEYS.GOOGLE_ACCESS_TOKEN)
    );
  }

  getGoogleRefreshToken() {
    return (
      this.googleRefreshToken ||
      localStorage.getItem(LOCAL_STORAGE_KEYS.GOOGLE_REFRESH_TOKEN)
    );
  }

  getUserMetaData() {
    return (
      this.userMetaData ||
      JSON.parse(
        localStorage.getItem(LOCAL_STORAGE_KEYS.USER_META_DATA) || "{}"
      )
    );
  }

  saveAccessToken(token: string) {
    if (!token) {
      return;
    }
    this.accessToken = token;
    localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, token);
  }

  saveRefreshToken(token: string) {
    if (!token) {
      return;
    }
    this.refreshToken = token;
    localStorage.setItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN, token);
  }

  saveGoogleAuthToken(token: string) {
    if (!token) {
      return;
    }
    this.googleAuthToken = token;
    localStorage.setItem(LOCAL_STORAGE_KEYS.GOOGLE_AUTH_TOKEN, token);
  }

  saveGoogleAccessToken(token: string) {
    if (!token) {
      return;
    }
    this.googleAccessToken = token;
    localStorage.setItem(LOCAL_STORAGE_KEYS.GOOGLE_ACCESS_TOKEN, token);
  }

  saveGoogleRefreshToken(token: string) {
    if (!token) {
      return;
    }
    this.googleRefreshToken = token;
    localStorage.setItem(LOCAL_STORAGE_KEYS.GOOGLE_REFRESH_TOKEN, token);
  }

  isValid = (token: string): boolean => {
    const claim = parseJWTToken(token);
    if (!claim) {
      return false;
    }
    const currentSeconds = Math.floor(new Date().valueOf() / 1000);
    return currentSeconds < claim.exp;
  };

  private proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  // Schedule a silent token refresh SECONDS_TO_REFRESH_TOKEN seconds before
  // the access token expires. The supplied triggerRefresh callback is expected
  // to call /token/refresh/ and, on success, call scheduleProactiveRefresh
  // again with the new token to keep the timer rolling.
  scheduleProactiveRefresh(
    accessToken: string,
    triggerRefresh: () => void
  ) {
    if (this.proactiveRefreshTimer !== null) {
      clearTimeout(this.proactiveRefreshTimer);
      this.proactiveRefreshTimer = null;
    }
    const claim = parseJWTToken(accessToken);
    if (!claim) return;
    const msUntilExpiry = claim.exp * 1000 - Date.now();
    const msUntilRefresh = msUntilExpiry - SECONDS_TO_REFRESH_TOKEN * 1000;
    if (msUntilRefresh > 0) {
      this.proactiveRefreshTimer = setTimeout(triggerRefresh, msUntilRefresh);
    }
  }

  cancelProactiveRefresh() {
    if (this.proactiveRefreshTimer !== null) {
      clearTimeout(this.proactiveRefreshTimer);
      this.proactiveRefreshTimer = null;
    }
  }

  // Broadcasts new tokens to all other open tabs so they can update
  // immediately — prevents the blacklist race condition where one tab
  // rotates the refresh token and another tab is left holding the old one.
  broadcastTokenUpdate(accessToken: string, refreshToken: string): void {
    if (!this.channel) return;
    this.channel.postMessage({ type: 'TOKEN_UPDATE', accessToken, refreshToken });
  }

  // Subscribe to token updates broadcast by sibling tabs.
  // Must be called once after silentRefresh is defined (see client.ts).
  subscribeToTokenUpdates(triggerRefresh: () => void): void {
    if (this.channel) return; // already subscribed
    if (typeof BroadcastChannel === 'undefined') return;
    try {
      this.channel = new BroadcastChannel('orbit_token_sync');
      this.channel.onmessage = (event) => {
        const data = event?.data;
        if (
          data &&
          data.type === 'TOKEN_UPDATE' &&
          typeof data.accessToken === 'string' &&
          typeof data.refreshToken === 'string'
        ) {
          this.saveAccessToken(data.accessToken);
          this.saveRefreshToken(data.refreshToken);
          this.scheduleProactiveRefresh(data.accessToken, triggerRefresh);
        }
      };
    } catch {
      this.channel = null;
    }
  }

  private saveUserMetaData(tokenData: {}) {
    const data = filterKeys(tokenData, this.requiredDataKeys);
    this.userMetaData = data;
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.USER_META_DATA,
      JSON.stringify(data)
    );
  }

  saveGoogleCodes = (res: {
    access_token: string;
    refresh_token: string;
    code: string;
  }) => {
    this.saveGoogleAccessToken(res?.access_token);
    this.saveGoogleRefreshToken(res?.refresh_token);
    this.saveGoogleAuthToken(res?.code);
  };

  login = async (res: {
    access_token: string;
    is_access_requested?: boolean;
    silentRefresh?: SilentRefreshFn;
  }) => {
    // Wipe any stale data from a previous session that ended without a clean logout
    // (crash, tab kill, power-off, etc.) before writing fresh tokens.
    this.emptyLocalStorage();
    localStorage.removeItem('lastSessionId');
    localStorage.removeItem('loginTimestamp');
    sessionStorage.removeItem('userSession');
    sessionStorage.removeItem('sessionId');

    let tokenData = await fetchUserData(
      res?.access_token,
      res?.is_access_requested
    );
    this.saveUserMetaData(tokenData);
    this.saveAccessToken(tokenData?.access_token);
    this.saveRefreshToken(tokenData?.refresh_token);
    this.authenticated = this.isValid(tokenData.access_token);
    if (tokenData?.access_token && res.silentRefresh) {
      this.scheduleProactiveRefresh(tokenData.access_token, res.silentRefresh);
    }
    return { isAuthenticated: this.authenticated, userDetails: tokenData };
  };

  private removeKeyFromLocalStorage = (key: string) => {
    localStorage.removeItem(key);
  };

  private emptyLocalStorage = () => {
    const filteredKeys = Object.keys(LOCAL_STORAGE_KEYS).filter((key) =>
      REMOVE_TOKENS.LOCAL_STORAGE_KEYS.includes(key)
    );
    filteredKeys.forEach((key) => {
      this.removeKeyFromLocalStorage(key);
    });
  };

  subscribeTokenRefresh(cb: (token: string | null) => void) {
    this.refreshSubscribers.push(cb);
  }

  onRefreshed(token: string | null) {
    this.refreshSubscribers.forEach((cb) => cb(token));
    this.refreshSubscribers = [];
  }

  private resetParams = () => {
    this.accessToken = null;
    this.refreshToken = null;
    // this.googleAccessToken = null;
    // this.googleRefreshToken = null;
    // this.googleAuthToken = null;
    this.authenticated = false;
    this.isRefreshing = false;
    this.refreshSubscribers = [];
  };

  discardSession() {
    this.cancelProactiveRefresh();
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    googleLogout();
    this.emptyLocalStorage();
    // ✅ Clear sessionStorage (audit tracking)
    sessionStorage.removeItem('userSession');
    sessionStorage.removeItem('sessionId');
    this.resetParams();
  }
}
